import React from 'react';
import {convertToRoman} from 'app/helpers/convertToRoman';
import styled from 'styled-components';
import {Subheading} from 'app/styles/heading';
import {COLORS, FONTS, SIZES, Typography} from '@fupa/fupa-uikit';

const Wrapper = styled.div`
  background-color: ${COLORS.white};
  padding-bottom: ${SIZES['8']};
`;

const CustomCaption = styled(Typography)`
  display: block;
  padding: ${SIZES['4']} 0;
`;

const Container = styled.div`
  padding: 0 ${SIZES['16']};
`;

const StrongFont = styled.span`
  font-weight: ${FONTS.bold};
`;

const getStandingsMetaInformation = (key, defaultTitle, districtName) => {
  const getMetaInformation = key =>
    ({
      total: {title: 'Tabelle', metaDescription: 'zur Hin- und Rückrunde'},
      home: {title: 'Heimtabelle', metaDescription: 'zu allen Heimspielen in der Hin- und Rückrunde'},
      away: {title: 'Auswärtstabelle', metaDescription: 'zu allen Auswärtsspielen in der Hin- und Rückrunde'},
      firstRound: {title: 'Hinrundentabelle', metaDescription: 'zur Hinrunde'},
      secondRound: {title: 'Rückrundentabelle', metaDescription: 'zur Rückrunde'},
      form: {title: 'Formtabelle', metaDescription: 'zur aktuellen Form der Mannschaften'},
    }[key]);

  const {title, metaDescription} = getMetaInformation(key);

  const meta = [
    {
      name: 'description',
      content: `Die ausführliche ${title} mit allen detaillierten Informationen ${metaDescription} der Liga ${defaultTitle} - Kreis ${districtName}.`,
    },
  ];
  return {title, meta};
};

const StandingInformation = ({helptext, standings}) => {
  const penalties = standings
    .map(team => (team.penaltyPoints && team.penaltyPoints !== 0 ? team : null))
    .filter(team => team);
  const flagOutOfCompetition = standings.find(team => team.team?.flags?.includes('outofcompetition'));
  const flagWithdraw = standings.find(team => team.team?.flags?.includes('withdraw'));

  const helptextWithdraw = flagWithdraw ? (
    <Container>
      <CustomCaption variant='caption1'>
        Vermerk "zg." (zurückgezogen): Mannschaft wurde während der Saison aus dem Spielbetrieb zurückgezogen.
      </CustomCaption>
    </Container>
  ) : null;

  const helptextOutOfCompetition = flagOutOfCompetition ? (
    <Container>
      <CustomCaption variant='caption1'>
        Vermerk "o.W." (ohne Wertung): Ergebnisse dieser Mannschaft werden nicht in der Tabelle gewertet.
      </CustomCaption>
    </Container>
  ) : null;

  const getHelptext = helptext => {
    const refactoredHelptext = helptext ? helptext.split(/\r?\n/g).filter(sentence => sentence) : null;
    return refactoredHelptext
      ? refactoredHelptext.map(sentence => (
          <CustomCaption variant='caption1' key={sentence}>
            {sentence}
          </CustomCaption>
        ))
      : null;
  };

  const getPenalties = penaltyTeams => {
    const data = [];
    penaltyTeams.map(team => {
      if (team.team) {
        const teamName =
          team.team.level > 1 ? `${team.team.name.full} ${convertToRoman(team.team.level)}` : team.team.name.full;
        data.push({name: teamName, points: team.penaltyPoints});
      }
    });
    return data.map(team => (
      <CustomCaption variant='caption1' key={team.name}>
        <StrongFont>{team.name}:</StrongFont>&nbsp;{team.points} Punkte Abzug
      </CustomCaption>
    ));
  };

  let showPenalties = null;
  if (penalties.length) {
    const penaltyMarkup = getPenalties(penalties);
    showPenalties = (
      <div>
        <Subheading variant='subheading2'>* Punktabzüge</Subheading>
        <Container>{penaltyMarkup}</Container>
      </div>
    );
  }

  let showHelptext = null;
  if (helptext) {
    const helptextMarkup = getHelptext(helptext);
    showHelptext = (
      <div>
        <Subheading variant='subheading2'>Anmerkungen</Subheading>
        <Container>{helptextMarkup}</Container>
        {helptextWithdraw}
        {helptextOutOfCompetition}
      </div>
    );
  }

  return (
    <Wrapper>
      {showPenalties}
      {showHelptext}
    </Wrapper>
  );
};

const FormInformation = () => {
  return (
    <Wrapper>
      <div>
        <Subheading variant='subheading2'>Anmerkungen</Subheading>
        <Container>
          <CustomCaption variant='caption1'>
            * Die Formtabelle berücksichtigt ausschließlich die letzten fünf Spiele eines jeden Teams. Das aktuellste
            Spiel ist ganz rechts.
            <p />
            In die Wertung gehen ein:
            <ul>
              <li> die erzielten Punkte der letzten fünf Spiele</li>
              <li> die Stärke der jeweiligen Spieltagsgegner anhand deren Tabellenpositionen</li>
              <li>
                {' '}
                Zeitfaktor: Das aktuellste Spiel wird stärker gewichtet als das am weitesten zurückliegende der fünf
                Spiele
              </li>
            </ul>
          </CustomCaption>
        </Container>
      </div>
    </Wrapper>
  );
};

export {StandingInformation, FormInformation, getStandingsMetaInformation};
